import jsPDF from 'jspdf';
import 'svg2pdf.js'; // parchea jsPDF con doc.svg(element, {x,y,width,height})
import html2canvas from 'html2canvas';

/**
 * PDFs de Dashboard/Ejecutivo -- ambos reportes estilo SaaS (portada + 1
 * página FULLSCREEN por gráfico, capturado con html2canvas). Ver
 * generateSaasReportPdf (Dashboard, personalizado) y
 * generateExecutiveSaasPdf (Ejecutivo, gráficos fijos) más abajo.
 *
 * Contrato de timing: cada gráfico se busca en el DOM (`querySelector`)
 * MIENTRAS su tab está activa. El caller (DashboardPage) es responsable de
 * cambiar de tab y esperar a que cargue ANTES de llamar a addSaasChartPage
 * -- si se intenta después de cambiar de tab, Radix ya desmontó el nodo y
 * no hay card que capturar (se muestra un placeholder, no revienta).
 */

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 20;
const CONTENT_W = PAGE_W - 2 * MARGIN;
const ACCENT = '#1B9CEA';
const INK = '#141414';
const GRAY = '#8a8a8a';
const LIGHT_GRAY = '#c8c8c8';

// Usados solo por addResumenEjecutivo (grid de KPIs 2 columnas).
const CARD_GUTTER = 10;
const CARD_W = (CONTENT_W - CARD_GUTTER) / 2;

async function loadImageAsDataUrl(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('No se pudo cargar la imagen');
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function resolveLogoUrl(logoUrl) {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http') || logoUrl.startsWith('data:')) return logoUrl;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return `${base}${logoUrl}`;
}

const ACCENT_MAP = {
  á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u',
  Á: 'A', É: 'E', Í: 'I', Ó: 'O', Ú: 'U',
  ñ: 'n', Ñ: 'N', ü: 'u', Ü: 'U',
};

function sanitizeFilename(name) {
  const withoutAccents = String(name || 'Empresa').replace(/[áéíóúÁÉÍÓÚñÑüÜ]/g, (c) => ACCENT_MAP[c] || c);
  const clean = withoutAccents.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return clean || 'Empresa';
}

export function createExecutiveDoc() {
  return new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
}

/**
 * Página 2: resumen ejecutivo. `kpis` = [{label, value, sub?}], YA filtrado
 * por el caller a solo los KPIs que existen en datos reales (nunca inventar).
 */
export function addResumenEjecutivo(doc, kpis) {
  doc.addPage();
  let y = MARGIN + 10;
  doc.setFontSize(16);
  doc.setTextColor(INK);
  doc.text('Resumen Ejecutivo', MARGIN, y);
  doc.setDrawColor(ACCENT);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, y + 3, MARGIN + 40, y + 3);
  y += 20;

  if (!kpis || !kpis.length) {
    doc.setFontSize(10);
    doc.setTextColor(GRAY);
    doc.text('Sin KPIs disponibles para los cuadrantes visibles.', MARGIN, y);
    return;
  }

  const cardH = 32;
  let col = 0;
  let rowY = y;
  kpis.forEach((kpi) => {
    const x = MARGIN + col * (CARD_W + CARD_GUTTER);
    doc.setDrawColor(LIGHT_GRAY);
    doc.setLineWidth(0.3);
    doc.rect(x, rowY, CARD_W, cardH);

    doc.setFontSize(9);
    doc.setTextColor(GRAY);
    doc.text(kpi.label, x + 5, rowY + 9);

    doc.setFontSize(18);
    doc.setTextColor(INK);
    doc.text(String(kpi.value), x + 5, rowY + 22);

    if (kpi.sub) {
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.text(kpi.sub, x + 5, rowY + 28);
    }

    col += 1;
    if (col >= 2) {
      col = 0;
      rowY += cardH + 8;
    }
  });
}

/* =========================================================================
 * Motor SaaS compartido -- "Descargar Dashboard" (generateSaasReportPdf,
 * personalizado) y "Descargar Reporte Ejecutivo" (generateExecutiveSaasPdf,
 * gráficos fijos) usan las MISMAS piezas de abajo: portada + UNA página
 * FULLSCREEN por gráfico/tabla + header/footer. Reemplazan el grid 2x2
 * anterior de ambos reportes (generateDashboardPdf y el viejo
 * addChartToSection/addPortada/finalizeHeaderFooter, todos retirados).
 *
 * Captura: SIEMPRE raster (html2canvas), nunca SVG. Con 1 card por página
 * no hay presión de espacio que obligue a vectorizar, y una sola ruta de
 * captura sirve por igual para gráficos Y tablas (mkt-table-budget,
 * ventas-table-vendors, dir-chart-exec). La card se captura tal cual está
 * en pantalla -- tema OSCURO de la app -- y se enmarca en una card CLARA
 * sobre el fondo blanco de la página (no se re-renderiza en tema claro,
 * fuera de alcance): se lee como un screenshot insertado en el reporte, no
 * como un choque de color.
 * ========================================================================= */

const SAAS_HEADER_BG = '#f5f5f5';
const SAAS_HEADER_BORDER = '#dddddd';
const SAAS_CHART_BOX_H = 120;
const SAAS_CHART_BOX_W = CONTENT_W; // 170mm (margen de 20mm a cada lado)

function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function buildSaasReportFilename(companyName) {
  return `${sanitizeFilename(companyName)}_Dashboard_${todayIso()}.pdf`;
}

export function buildExecutiveSaasFilename(companyName) {
  return `${sanitizeFilename(companyName)}_Executive_Report_${todayIso()}.pdf`;
}

/** "2026-08-01" -> "01/08/2026". Nunca inventa un rango si falta alguna fecha. */
export function formatPeriodLabel(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return null;
  const fmt = (s) => {
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
  };
  return `Período: ${fmt(dateFrom)} — ${fmt(dateTo)}`;
}

/** Captura un nodo del DOM a JPEG en memoria. */
async function captureCardRaster(cardEl) {
  const canvas = await html2canvas(cardEl, {
    backgroundColor: '#1F2833', scale: 2, useCORS: true, logging: false,
  });
  if (!canvas.width || !canvas.height) return null;
  return { imgData: canvas.toDataURL('image/jpeg', 0.85), width: canvas.width, height: canvas.height };
}

/**
 * Portada compartida por Dashboard y Ejecutivo: logo del cliente grande +
 * título + empresa + fecha + período. `title` distingue el reporte
 * ("Dashboard Report" vs "Executive Report") -- todo lo demás es idéntico.
 */
async function addSaasCover(doc, { title = 'Dashboard Report', companyName, companyLogoUrl, periodLabel }) {
  const resolvedLogo = resolveLogoUrl(companyLogoUrl);
  if (resolvedLogo) {
    try {
      const dataUrl = await loadImageAsDataUrl(resolvedLogo);
      const fmt = dataUrl.includes('image/png') ? 'PNG' : 'JPEG';
      doc.addImage(dataUrl, fmt, (PAGE_W - 120) / 2, 40, 120, 40);
    } catch {
      // logo opcional: si falla la carga, la portada se genera igual sin logo
    }
  }

  let y = 115;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(INK);
  doc.text(title, PAGE_W / 2, y, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  y += 14;
  doc.setFontSize(14);
  doc.setTextColor('#3c3c3c');
  doc.text(companyName || '—', PAGE_W / 2, y, { align: 'center' });

  y += 9;
  doc.setFontSize(11);
  doc.setTextColor(GRAY);
  doc.text(`Generado el ${new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}`, PAGE_W / 2, y, { align: 'center' });

  if (periodLabel) {
    y += 7;
    doc.text(periodLabel, PAGE_W / 2, y, { align: 'center' });
  }

  y += 14;
  doc.setDrawColor(ACCENT);
  doc.setLineWidth(0.8);
  doc.line(PAGE_W / 2 - 30, y, PAGE_W / 2 + 30, y);
}

/**
 * Dibuja el índice EN la página 2 (ya reservada en blanco por el caller).
 * Se llama al final, cuando ya se conocen los números de página reales de
 * cada sección -- por eso no se arma "sobre la marcha".
 */
function drawSaasIndex(doc, sectionMarks) {
  doc.setPage(2);
  let y = MARGIN + 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(INK);
  doc.text('Índice', MARGIN, y);
  doc.setDrawColor(ACCENT);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, y + 3, MARGIN + 30, y + 3);
  y += 16;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  sectionMarks.forEach(({ label, page }) => {
    doc.setTextColor('#333333');
    doc.text(label, MARGIN, y);
    doc.setTextColor(GRAY);
    doc.text(String(page), PAGE_W - MARGIN, y, { align: 'right' });
    doc.setDrawColor(LIGHT_GRAY);
    doc.setLineWidth(0.2);
    doc.line(MARGIN, y + 2.5, PAGE_W - MARGIN, y + 2.5);
    y += 9;
  });
}

/**
 * Una página por gráfico/tabla: título descriptivo + captura FULLSCREEN
 * enmarcada + contexto (2-3 líneas, desde dashboardSections.js). Devuelve el
 * número de página real para que el caller arme el índice.
 */
async function addSaasChartPage(doc, { chartId, label, description, tabLabel }) {
  doc.addPage();
  const pageNum = doc.internal.getNumberOfPages();

  let y = MARGIN + 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(INK);
  doc.text(label, MARGIN, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(GRAY);
  doc.text(tabLabel, MARGIN, y + 6);

  const boxY = y + 14;
  const boxH = SAAS_CHART_BOX_H;
  const boxW = SAAS_CHART_BOX_W;
  const boxX = MARGIN;

  // Marco claro tipo "card": aísla la captura (tema oscuro real de la app)
  // del fondo blanco de la página -- se lee como un screenshot insertado a
  // propósito, no como un choque de color crudo.
  doc.setFillColor('#fafafa');
  doc.setDrawColor(LIGHT_GRAY);
  doc.setLineWidth(0.3);
  doc.roundedRect(boxX, boxY, boxW, boxH, 3, 3, 'FD');

  const cardEl = document.querySelector(`[data-testid="${chartId}"]`);
  if (cardEl) {
    try {
      const raster = await captureCardRaster(cardEl);
      if (raster) {
        const pad = 4;
        const innerW = boxW - pad * 2;
        const innerH = boxH - pad * 2;
        const scale = Math.min(innerW / raster.width, innerH / raster.height);
        const w = raster.width * scale;
        const h = raster.height * scale;
        const offsetX = boxX + (boxW - w) / 2;
        const offsetY = boxY + (boxH - h) / 2;
        doc.addImage(raster.imgData, 'JPEG', offsetX, offsetY, w, h);
      } else {
        doc.setFontSize(10);
        doc.setTextColor(GRAY);
        doc.text('No se pudo generar la vista de este gráfico', boxX + boxW / 2, boxY + boxH / 2, { align: 'center' });
      }
    } catch (e) {
      console.warn(`[pdfExecutive] no se pudo capturar "${label}" (SaaS report):`, e);
      doc.setFontSize(10);
      doc.setTextColor(GRAY);
      doc.text('No se pudo generar la vista de este gráfico', boxX + boxW / 2, boxY + boxH / 2, { align: 'center' });
    }
  } else {
    doc.setFontSize(10);
    doc.setTextColor(GRAY);
    doc.text('Cuadrante no disponible en esta vista', boxX + boxW / 2, boxY + boxH / 2, { align: 'center' });
  }

  if (description) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor('#333333');
    const lines = doc.splitTextToSize(description, boxW);
    doc.text(lines, boxX, boxY + boxH + 10);
  }

  return pageNum;
}

/**
 * Pasada final, compartida por Dashboard y Ejecutivo: header (logo cliente
 * 25mm top-right + `reportLabel` + fecha) y footer ("Página N de M" |
 * "Generado por Dealider" | "Confidencial") en TODAS las páginas excepto la
 * portada. El logo se carga UNA vez; si falla (CORS, ausente) el header se
 * arma igual sin logo.
 */
async function finalizeSaasReport(doc, { companyName, companyLogoUrl, reportLabel = 'Dashboard Report' }) {
  const totalPages = doc.internal.getNumberOfPages();
  const nowStr = new Date().toLocaleDateString('es-MX');

  let logoDataUrl = null;
  let logoFmt = 'PNG';
  const resolvedLogo = resolveLogoUrl(companyLogoUrl);
  if (resolvedLogo) {
    try {
      logoDataUrl = await loadImageAsDataUrl(resolvedLogo);
      logoFmt = logoDataUrl.includes('image/png') ? 'PNG' : 'JPEG';
    } catch {
      // logo opcional
    }
  }

  for (let p = 2; p <= totalPages; p += 1) {
    doc.setPage(p);

    doc.setFillColor(SAAS_HEADER_BG);
    doc.rect(0, 0, PAGE_W, 14, 'F');
    doc.setDrawColor(SAAS_HEADER_BORDER);
    doc.setLineWidth(0.3);
    doc.line(0, 14, PAGE_W, 14);
    if (logoDataUrl) doc.addImage(logoDataUrl, logoFmt, PAGE_W - MARGIN - 25, 2, 25, 10);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(GRAY);
    doc.text(reportLabel, MARGIN, 6);
    doc.text(companyName || '', MARGIN, 11);

    doc.setFillColor(SAAS_HEADER_BG);
    doc.rect(0, PAGE_H - 14, PAGE_W, 14, 'F');
    doc.line(0, PAGE_H - 14, PAGE_W, PAGE_H - 14);
    doc.setFontSize(8);
    doc.setTextColor(GRAY);
    doc.text(`Página ${p} de ${totalPages}`, MARGIN, PAGE_H - 6);
    doc.text(`Generado por Dealider · ${nowStr}`, PAGE_W / 2, PAGE_H - 6, { align: 'center' });
    doc.text('Confidencial', PAGE_W - MARGIN, PAGE_H - 6, { align: 'right' });
  }
}

/**
 * Orquestador de "Descargar Dashboard": portada -> índice (reservado en
 * blanco, se llena al final) -> 1 página por gráfico/tabla visible -> header
 * y footer en pasada final. Mismo contrato de timing que exportExecutive:
 * `switchTab` lo controla el caller (DashboardPage, dueño de las Tabs de
 * Radix) -- cada página se arma MIENTRAS su tab está activa.
 */
export async function generateSaasReportPdf({ company, userName, visibleSections, switchTab, waitMs = 1800, periodLabel }) {
  if (!visibleSections?.length) throw new Error('No hay secciones visibles para exportar');
  void userName; // el reporte SaaS no imprime el usuario (portada tipo Stripe/Mixpanel, sin firma personal)

  const doc = createExecutiveDoc();
  await addSaasCover(doc, { companyName: company?.name, companyLogoUrl: company?.logo, periodLabel });
  doc.addPage(); // página 2: índice, se dibuja al final (necesita los números de página reales)

  const sectionMarks = [];
  for (const section of visibleSections) {
    // eslint-disable-next-line no-await-in-loop
    await switchTab(section.key);
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, waitMs));

    let firstPageOfSection = null;
    for (const chart of (section.charts || [])) {
      // eslint-disable-next-line no-await-in-loop
      const pageNum = await addSaasChartPage(doc, {
        chartId: chart.id, label: chart.label, description: chart.description, tabLabel: section.label,
      });
      if (firstPageOfSection == null) firstPageOfSection = pageNum;
    }
    if (firstPageOfSection != null) sectionMarks.push({ label: section.label, page: firstPageOfSection });
  }

  drawSaasIndex(doc, sectionMarks);
  await finalizeSaasReport(doc, { companyName: company?.name, companyLogoUrl: company?.logo });

  doc.save(buildSaasReportFilename(company?.name));
}

/**
 * Orquestador de "Descargar Reporte Ejecutivo": misma arquitectura SaaS que
 * generateSaasReportPdf (portada + 1 página fullscreen por gráfico +
 * header/footer), pero con dos diferencias deliberadas:
 *  (1) gráficos FIJOS -- `fixedCharts` = [{section:{key,label}, chart:{id,
 *      label,description}}], uno por tab disponible por LICENCIA
 *      (gatedSections del caller), NUNCA filtrado por dashboard_prefs del
 *      usuario (el Ejecutivo ignora la personalización a propósito).
 *  (2) página 2 = resumen de KPIs reales (addResumenEjecutivo), no un
 *      índice -- con solo 5-6 páginas de contenido un índice aporta poco;
 *      los KPIs sí son el resumen ejecutivo que este reporte promete.
 */
export async function generateExecutiveSaasPdf({ company, userName, fixedCharts, kpis, switchTab, waitMs = 1800, periodLabel }) {
  if (!fixedCharts?.length) throw new Error('No hay secciones disponibles para el reporte ejecutivo');
  void userName; // igual que el Dashboard SaaS: portada sin firma personal

  const doc = createExecutiveDoc();
  await addSaasCover(doc, { title: 'Executive Report', companyName: company?.name, companyLogoUrl: company?.logo, periodLabel });
  addResumenEjecutivo(doc, kpis);

  for (const { section, chart } of fixedCharts) {
    // eslint-disable-next-line no-await-in-loop
    await switchTab(section.key);
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, waitMs));
    // eslint-disable-next-line no-await-in-loop
    await addSaasChartPage(doc, { chartId: chart.id, label: chart.label, description: chart.description, tabLabel: section.label });
  }

  await finalizeSaasReport(doc, { companyName: company?.name, companyLogoUrl: company?.logo, reportLabel: 'Executive Report' });

  doc.save(buildExecutiveSaasFilename(company?.name));
}
